import {
  ContactId,
  ShipReplacementTo,
  ReplacementType,
  TypeOfLoss,
  WaysToContact,
  LossLocation,
} from "../FieldIds";
import warningIcon from "../../../assets/warning-icon.svg";
import { useState } from "react";
import StyledRadioButton from "../../common/inputs/StyledRadioButton";
import Select from "../../common/inputs/Select";
import { LostStolenFormState } from "../LostStolen";
import styles from "./LostStolenForm.module.css";
import BaseModal from "../../common/modals/BaseModal/BaseModal";
import StyledButton from "../../common/buttons/StyledButton";
import PhoneNumberInput from "../../common/inputs/PhoneNumberInput";
import StyledInput from "../../common/inputs/StyledInput";
import StyledZipcodeInput from "../../common/inputs/StyledZipcodeInput";
import classNames from "classnames";
import DateInput from "../../common/inputs/DateInput";
import {
  Option,
  LostStolenOptionsResponse,
  DateString,
  ValidationErrors,
} from "../../../lib/api/models";
import { INPUT_MASKS, maskInput } from "../../../lib/formatting";
import { format } from "date-fns/format";
import Tooltip from "../../common/Tooltip";
import ZipcodeLookupModal from "../../common/modals/ZipcodeLookupModal/ZipcodeLookupModal";
import Spinner from "../../common/Spinner";
import {
  additionalInfoRegex,
  ADDRESS_LINE_MAX_LENGTH,
  addressRegex,
  CHANGE_FIELD_CODES,
  CITY_MAX_LENGTH,
  cityRegex,
  FRAUD_CHARGES_MAX_LENGTH,
  fraudChargesRegex,
  PHONE_VALUE_LENGTH,
  SPECIAL_FISERV_PHONE_NUMBERS,
  ZIPCODE_LENGTH,
} from "../constants";
import ValidationMessage from "../../common/ValidationMessage/ValidationMessage";

const strings = {
  contact: "Contact*",
  select: "Select",
  yes: "Yes",
  no: "No",
  typeOfLoss: "Type of Loss*",
  fraudOnAccount: "Fraud on Account?*",
  transferToSecurity: "Xfer'd to Security?*",
  cid: "CID*",
  bestWayToContact: "Best Way to Contact",
  phoneNumber: "Phone Number*",
  shipReplacementTo: "Ship Replacement To*",
  shipReplacementToTooltipTitle: "SHIP REPLACEMENT TO:",
  shipReplacementToBillingAddressTooltipLabel: "Billing address",
  shipReplacementToBillingAddressTooltipContent:
    "is the address on file for the customer.",
  shipReplacementToTempAddressTooltipLabel: "Temporary address",
  shipReplacementToTempAddressTooltipContent:
    "is an alternate address where the replacement card can be sent.",
  addressChangeToday: "Address Change Today?*",
  newAddressVerify: "New Addr Vrfy w Security?*",
  tempAddressVerify: "Temp Addr Vrfy w Security?*",
  reasonForTempAddress: "Reason for Temp Address*",
  tempAddress1: "Temp Address 1*",
  tempAddress2: "Temp Address 2",
  addressInvalidChars: "Address line contains invalid characters",
  zip: "Zip*",
  city: "City*",
  cityInvalidChars:
    "City on Temporary Address is blank or contains invalid characters.",
  state: "State*",
  replacementOptions: "Replacement Options*",
  replacementOptionsTooltipTitle: "REPLACEMENT OPTIONS:",
  replacementOptionsStandardLabel: "Standard:",
  replacementOptionsStandardText: (replacementFee: string) =>
    `Delivery is usually in 10-14 days and costs ${replacementFee}. (No Fee for SCRA and MLA)`,
  replacementOptionsRushFedexLabel: "Rush FedEx:",
  replacementOptionsRushFedexText: (replacementFee: string, rushFee: string) =>
    `Delivery is usually 3 business days, to a street address and the cost is ${replacementFee} plus ${rushFee} (rush fee) (No Fee for SCRA and MLA). Signature required.`,
  replacementOptionsRushPostalLabel: "Rush Postal:",
  replacementOptionsRushPostalText: (replacementFee: string, rushFee: string) =>
    `Delivery is less than 10-14 days but may be more than 3 days and the cost is ${replacementFee} plus ${rushFee} (rush fee) (No Fee for SCRA and MLA) (PO Box and APO addresses) No signature required.`,
  replacementOptionsFedExSaturdayLabel: "FedEx Saturday:",
  replacementOptionsFedExSaturdayText: (
    replacementFee: string,
    rushFee: string,
  ) =>
    `Saturday delivery to the Card Member, Saturday delivery can only be ordered after 5:00 pm Tuesday and on or before 5:00 pm Wednesday. Cost is ${replacementFee} plus ${rushFee} (rush fee) (No Fee for SCRA and MLA) Signature required.`,
  waiveReplacementFee: "Waive Replacement Fee?*",
  waiveRushFee: "Waive Rush Fee?*",
  dateLost: "Date Lost*",
  futureDateLostErrorMessage: "Date Lost cannot be in the future",
  lossLocation: "Loss Location*",
  stateOfLoss: "US State of Loss*",
  pinCompromised: "PIN Compromised?*",
  fraudCharges: "Fraud Charges*",
  fraudChargesInvalidChars: "Fraud Charges contains invalid characters.",
  transferToSecurityModalTitle: "Transfer to Security",
  transferToSecurityModalDescription:
    "If Transfer for Security was successful, click “Close Tool” to End.",
  closeTool: "Close Tool",
  goBack: "Go Back",
  additionalInfo: "Additional Info",
  changingFieldName: (messageCode: string, fieldName: string) =>
    `${messageCode} - Changing the value of "${fieldName}" may impact other fields that you have completed. Do you want to continue?`,
  additionalInfoValidation:
    "A_0522 - Fraud Charges or Additional Information contains invalid characters.",
};

function TypeOfLossComponent({
  options,
  typeOfLoss,
  onChange,
  disabled,
  hideFraudApp,
}: {
  options: LostStolenOptionsResponse;
  typeOfLoss?: TypeOfLoss;
  onChange: (value: TypeOfLoss) => void;
  disabled?: boolean;
  hideFraudApp?: boolean;
}) {
  return (
    <>
      <Select
        variant="horizontal"
        options={(hideFraudApp
          ? options.lossTypes.filter((lt) => lt.id !== TypeOfLoss.FraudApp)
          : options.lossTypes
        ).map((lt) => ({
          value: lt.id as TypeOfLoss,
          label: lt.value,
        }))}
        selectedValue={typeOfLoss}
        onChange={onChange}
        label={strings.typeOfLoss}
        labelClassName={styles.label}
        placeholder={strings.select}
        disabled={disabled}
      />
      <div className={styles.divider} />
    </>
  );
}

function FraudOnAccountComponent({
  onChange,
  fraudOnAccount,
  disabled,
  hideNoButton,
}: {
  onChange: (value: boolean) => void;
  fraudOnAccount?: boolean;
  disabled: boolean;
  hideNoButton?: boolean;
}) {
  return (
    <>
      <div className={styles.radioContainer}>
        <div className={classNames(styles.label, styles.radioLabel)}>
          {strings.fraudOnAccount}
        </div>
        <div>
          <StyledRadioButton
            className={styles.radioButton}
            checked={!!fraudOnAccount}
            label={strings.yes}
            disabled={disabled}
            onChange={() => onChange(true)}
          />
          {!hideNoButton ? (
            <StyledRadioButton
              className={styles.radioButton}
              checked={fraudOnAccount !== undefined && !fraudOnAccount}
              label={strings.no}
              disabled={disabled}
              onChange={() => onChange(false)}
            />
          ) : null}
        </div>
      </div>
      <div className={styles.divider} />
    </>
  );
}

function ShipReplacementToComponent({
  disabled,
  shipToTypes,
  selectedValue,
  onChange,
}: {
  disabled: boolean;
  shipToTypes: Option[];
  selectedValue?: ShipReplacementTo;
  onChange: (value: ShipReplacementTo) => void;
}) {
  return (
    <>
      <Select
        variant="horizontal"
        disabled={disabled}
        options={shipToTypes.map((stt) => ({
          label: stt.value,
          value: stt.id as ShipReplacementTo,
        }))}
        selectedValue={selectedValue}
        onChange={onChange}
        aria-label={strings.shipReplacementTo}
        label={
          <div className={styles.labelWithTooltip}>
            {strings.shipReplacementTo}
            <Tooltip>
              <p className={styles.tooltipText}>
                {strings.shipReplacementToTooltipTitle}
                <br />
                <u>{strings.shipReplacementToBillingAddressTooltipLabel}</u>
                {" " + strings.shipReplacementToBillingAddressTooltipContent}
                <br />
                <u>{strings.shipReplacementToTempAddressTooltipLabel}</u>
                {" " + strings.shipReplacementToTempAddressTooltipContent}
              </p>
            </Tooltip>
          </div>
        }
        labelClassName={styles.label}
        placeholder={strings.select}
      />
      <div className={styles.divider} />
    </>
  );
}

function ReplacementOptionsComponent({
  disabled,
  replacementTypes,
  replacementFee,
  rushFee,
  selectedValue,
  onChange,
}: {
  disabled: boolean;
  replacementTypes: Option[];
  replacementFee: string;
  rushFee: string;
  selectedValue?: ReplacementType;
  onChange: (value: ReplacementType) => void;
}) {
  return (
    <>
      <Select
        variant="horizontal"
        disabled={disabled}
        aria-label={strings.replacementOptions}
        label={
          <div className={styles.labelWithTooltip}>
            {strings.replacementOptions}
            <Tooltip>
              <p className={styles.tooltipText}>
                {strings.replacementOptionsTooltipTitle}
                <br />
                <u>{strings.replacementOptionsStandardLabel}</u>
                {" " + strings.replacementOptionsStandardText(replacementFee)}
                <br />
                <u>{strings.replacementOptionsRushFedexLabel}</u>
                {" " +
                  strings.replacementOptionsRushFedexText(
                    replacementFee,
                    rushFee,
                  )}
                <br />
                <u>{strings.replacementOptionsRushPostalLabel}</u>
                {" " +
                  strings.replacementOptionsRushPostalText(
                    replacementFee,
                    rushFee,
                  )}
                <br />
                <u>{strings.replacementOptionsFedExSaturdayLabel}</u>
                {" " +
                  strings.replacementOptionsFedExSaturdayText(
                    replacementFee,
                    rushFee,
                  )}
              </p>
            </Tooltip>
          </div>
        }
        labelClassName={styles.label}
        placeholder={strings.select}
        options={replacementTypes.map((rt) => ({
          value: rt.id as ReplacementType,
          label: rt.value,
        }))}
        selectedValue={selectedValue}
        onChange={onChange}
      />
      <div className={styles.divider} />
    </>
  );
}

type Props = {
  formState: LostStolenFormState;
  setFormState: React.Dispatch<React.SetStateAction<LostStolenFormState>>;
  onTransferToSecurity: () => void;
  options: LostStolenOptionsResponse;
  isSubmitting: boolean;
  validationMessages?: ValidationErrors;
  clearNetworkValidationMessage: (...keys: string[]) => void;
};

export default function LostStolenForm({
  formState,
  setFormState,
  onTransferToSecurity,
  options,
  isSubmitting,
  validationMessages,
  clearNetworkValidationMessage,
}: Props) {
  const [showTransferToSecurityModal, setShowTransferToSecurityModal] =
    useState(false);
  const [editedField, setEditedField] = useState<null | {
    name: string;
    code: string;
    onContinue: () => void;
  }>(null);
  const [zipcodeToLookup, setZipcodeToLookup] = useState<string | null>(null);

  const hasVeryLimitedAccess =
    formState.contactId === ContactId.Merchant ||
    formState.contactId === ContactId.ThirdPartyUnknownToCM;
  const hasLimitedAccess =
    formState.contactId === ContactId.AuthUser ||
    formState.contactId === ContactId.Parent ||
    formState.contactId === ContactId.ThirdPartyKnownToCM ||
    formState.contactId === ContactId.Other;

  const hasInactiveStatus =
    options.externalStatus === "B" || options.externalStatus === "Z";
  const hasLostStolenExtStatus =
    options.externalStatus === "L" || options.externalStatus === "U";
  const hasInvalidStatus =
    options.externalStatus === "C" ||
    options.externalStatus === "E" ||
    options.externalStatus === "F" ||
    options.externalStatus === "I";

  const hideFieldsIfFraudAppOrLostCard =
    formState.contactId === ContactId.Cardmember &&
    (hasInvalidStatus ||
      (options.externalStatus === "A" && options.reasonCode === "62") ||
      (options.externalStatus !== "" &&
        options.externalStatus !== "A" &&
        formState.typeOfLoss === TypeOfLoss.LostCard) ||
      formState.typeOfLoss === TypeOfLoss.FraudApp) &&
    formState.yesFraudOnAccount?.transferToSecurity === false;

  const hideFieldsIfYesFraudAndNotReceivedOrStolen =
    options.externalStatus === "A" &&
    options.reasonCode === "62" &&
    formState.contactId === ContactId.Cardmember &&
    (formState.typeOfLoss === TypeOfLoss.NotReceived ||
      formState.typeOfLoss === TypeOfLoss.Stolen) &&
    formState.yesFraudOnAccount?.transferToSecurity === false;

  const hideShipReplacementTo =
    hideFieldsIfFraudAppOrLostCard ||
    hideFieldsIfYesFraudAndNotReceivedOrStolen ||
    (hasInvalidStatus &&
      formState.contactId === ContactId.Cardmember &&
      formState.fraudOnAccount &&
      formState.yesFraudOnAccount?.transferToSecurity === false);

  const hideFieldsAfterFraudOnAccount =
    hasInvalidStatus &&
    formState.contactId === ContactId.Cardmember &&
    formState.typeOfLoss === TypeOfLoss.LostCard;

  const earlyTerminationForLimitedAccess =
    hasLimitedAccess &&
    (options.externalStatus === "" ||
      (options.externalStatus === "A" && options.reasonCode === "00"));

  const doNotPrefillStateOfLoss =
    hasLimitedAccess &&
    (options.externalStatus === "" ||
      (options.externalStatus === "A" && options.reasonCode === "00"));

  const onlyShowYesForWaiveReplacementFee =
    options.onlyYesForWaiveReplacementFee &&
    formState.contactId === ContactId.Cardmember &&
    formState.typeOfLoss === TypeOfLoss.LostCard &&
    formState.fraudOnAccount === false;

  const finalInfo = formState.earlyTermination
    ? formState.earlyTermination.finalInfo
    : hideShipReplacementTo
      ? formState?.yesFraudOnAccount?.noShipReplacementToFinalInfo
      : formState.fraudOnAccount
        ? formState.yesFraudOnAccount?.shipReplacementTo ===
          ShipReplacementTo.Billing
          ? formState.yesFraudOnAccount?.billing?.finalInfo
          : formState.yesFraudOnAccount?.temporary?.finalInfo
        : formState.noFraudOnAccount?.shipReplacementTo ===
            ShipReplacementTo.Billing
          ? formState.noFraudOnAccount?.billing?.finalInfo
          : formState.noFraudOnAccount?.temporary?.finalInfo;

  return (
    <>
      <Select
        variant="horizontal"
        disabled={isSubmitting}
        options={options.contactTypes.map((ct) => ({
          value: ct.id as ContactId,
          label: ct.value,
        }))}
        selectedValue={formState.contactId}
        onChange={(value: ContactId) => {
          const updateValue = () => {
            const prefillValuesForLimitedAccess =
              options.externalStatus === "" &&
              (value === ContactId.AuthUser ||
                value === ContactId.Other ||
                value === ContactId.Parent ||
                value === ContactId.ThirdPartyKnownToCM);
            const showLS0020 =
              (value === ContactId.ThirdPartyKnownToCM &&
                !hasInactiveStatus &&
                !hasLostStolenExtStatus &&
                !hasInvalidStatus &&
                !(
                  options.externalStatus === "A" && options.reasonCode === "62"
                )) ||
              ((value === ContactId.AuthUser ||
                value === ContactId.Parent ||
                value === ContactId.Other) &&
                options.externalStatus === "A" &&
                options.reasonCode !== "62");
            setFormState({
              contactId: value,
              typeOfLoss:
                value === ContactId.Merchant ||
                value === ContactId.ThirdPartyUnknownToCM
                  ? TypeOfLoss.LostCard
                  : undefined,
              earlyTermination:
                prefillValuesForLimitedAccess ||
                showLS0020 ||
                ((value === ContactId.Merchant ||
                  value === ContactId.ThirdPartyUnknownToCM) &&
                  (options.externalStatus === "" ||
                    (options.externalStatus === "A" &&
                      options.reasonCode !== "62")))
                  ? {
                      shipReplacementTo: ShipReplacementTo.Billing,
                      additionalInfo:
                        formState?.earlyTermination?.additionalInfo,
                      finalInfo: {
                        replacementOption: ReplacementType.Standard,
                        waiveReplacementFee: false,
                        stateOfLoss:
                          prefillValuesForLimitedAccess ||
                          showLS0020 ||
                          value === ContactId.Merchant ||
                          value === ContactId.ThirdPartyUnknownToCM ||
                          (options.externalStatus === "A" &&
                            options.reasonCode !== "62")
                            ? options.accountAddress.stateCode
                            : undefined,
                      },
                    }
                  : undefined,
            });
          };

          if (formState.contactId) {
            setEditedField({
              name: strings.contact,
              code: CHANGE_FIELD_CODES.contact,
              onContinue: updateValue,
            });
          } else {
            updateValue();
          }
        }}
        label={strings.contact}
        labelClassName={styles.label}
        placeholder={strings.select}
      />
      <div className={styles.divider} />
      {(options.isLoanAccount &&
        formState.contactId === ContactId.Cardmember) ||
      (formState.contactId === ContactId.Cardmember && hasInactiveStatus) ||
      ((hasLimitedAccess || hasVeryLimitedAccess) &&
        (hasInactiveStatus || hasLostStolenExtStatus || hasInvalidStatus)) ? (
        <>
          {hasVeryLimitedAccess &&
          (hasInactiveStatus || hasLostStolenExtStatus || hasInvalidStatus) ? (
            <TypeOfLossComponent
              options={options}
              typeOfLoss={TypeOfLoss.LostCard}
              onChange={() => {
                // do nothing
              }}
              disabled={true}
            />
          ) : null}
        </>
      ) : null}
      {hasVeryLimitedAccess &&
      (options.externalStatus === "" || options.externalStatus === "A") ? (
        <TypeOfLossComponent
          options={options}
          disabled={true}
          typeOfLoss={formState.typeOfLoss}
          onChange={() => {
            //nothing
          }}
        />
      ) : null}
      {(formState.contactId === ContactId.Cardmember &&
        !options.isLoanAccount &&
        !hasInactiveStatus) ||
      (hasLimitedAccess &&
        !hasInactiveStatus &&
        !hasLostStolenExtStatus &&
        !hasInvalidStatus &&
        !(options.externalStatus === "A" && options.reasonCode === "62")) ? (
        <TypeOfLossComponent
          options={options}
          typeOfLoss={formState.typeOfLoss}
          hideFraudApp={hasLimitedAccess}
          onChange={(value: TypeOfLoss) => {
            const updateValue = () => {
              const showLS0020 =
                (formState.contactId === ContactId.ThirdPartyKnownToCM &&
                  !hasInactiveStatus &&
                  !hasLostStolenExtStatus &&
                  !hasInvalidStatus &&
                  !options.isClosedAccount) ||
                (hasLimitedAccess && options.externalStatus === "A");
              setFormState((prev) => ({
                ...prev,
                typeOfLoss: value,
                fraudOnAccount:
                  !hasLimitedAccess &&
                  (value === TypeOfLoss.FraudApp ||
                    value === TypeOfLoss.Counterfeit ||
                    value === TypeOfLoss.AccountTakeover ||
                    value === TypeOfLoss.AllMailAndPhone)
                    ? true
                    : undefined,
                yesFraudOnAccount: undefined,
                noFraudOnAccount: undefined,
                earlyTermination:
                  earlyTerminationForLimitedAccess ||
                  showLS0020 ||
                  (hasLimitedAccess &&
                    (value === TypeOfLoss.NotReceived ||
                      value === TypeOfLoss.Counterfeit ||
                      value === TypeOfLoss.AccountTakeover ||
                      value === TypeOfLoss.AllMailAndPhone))
                    ? {
                        shipReplacementTo: ShipReplacementTo.Billing,
                        finalInfo: {
                          replacementOption: ReplacementType.Standard,
                          waiveReplacementFee:
                            value === TypeOfLoss.NotReceived ||
                            value === TypeOfLoss.Counterfeit ||
                            value === TypeOfLoss.AccountTakeover ||
                            value === TypeOfLoss.AllMailAndPhone
                              ? true
                              : earlyTerminationForLimitedAccess || showLS0020
                                ? false
                                : undefined,
                          lossLocation:
                            value === TypeOfLoss.Counterfeit ||
                            value === TypeOfLoss.AccountTakeover ||
                            value === TypeOfLoss.AllMailAndPhone
                              ? LossLocation.Unknown
                              : undefined,
                          stateOfLoss:
                            !doNotPrefillStateOfLoss &&
                            (earlyTerminationForLimitedAccess || showLS0020)
                              ? options.accountAddress.stateCode
                              : undefined,
                          dateLost: prev.earlyTermination?.finalInfo?.dateLost,
                          pinCompromised:
                            prev.earlyTermination?.finalInfo?.pinCompromised,
                        },
                      }
                    : undefined,
              }));
            };

            if (formState.typeOfLoss) {
              setEditedField({
                name: strings.typeOfLoss,
                code: CHANGE_FIELD_CODES.typeOfLoss,
                onContinue: updateValue,
              });
            } else {
              updateValue();
            }
          }}
          disabled={isSubmitting}
        />
      ) : null}
      {earlyTerminationForLimitedAccess ||
      ((formState.contactId === ContactId.ThirdPartyKnownToCM ||
        (hasLimitedAccess &&
          options.externalStatus === "A" &&
          options.reasonCode !== "62") ||
        formState.typeOfLoss !== undefined) &&
        ((hasVeryLimitedAccess &&
          formState.earlyTermination !== undefined &&
          (options.externalStatus === "" ||
            (options.externalStatus === "A" && options.reasonCode !== "62"))) ||
          (hasLimitedAccess &&
            formState.earlyTermination !== undefined &&
            !hasInactiveStatus &&
            !hasInvalidStatus &&
            !options.isClosedAccount &&
            !hasLostStolenExtStatus))) ? (
        <>
          <ShipReplacementToComponent
            disabled={true}
            shipToTypes={options.shipToTypes}
            selectedValue={formState.earlyTermination?.shipReplacementTo}
            onChange={() => {
              // do nothing
            }}
          />
          <ReplacementOptionsComponent
            disabled={true}
            replacementTypes={options.replacementTypes}
            replacementFee={options.replacementFee}
            rushFee={options.rushFee}
            selectedValue={
              formState.earlyTermination?.finalInfo?.replacementOption
            }
            onChange={() => {
              // do nothing
            }}
          />
        </>
      ) : null}
      {(formState.contactId === ContactId.Cardmember && !hasInactiveStatus) ||
      (formState.typeOfLoss &&
        !hasVeryLimitedAccess &&
        !earlyTerminationForLimitedAccess &&
        !(
          (formState.contactId === ContactId.ThirdPartyKnownToCM ||
            (hasLimitedAccess &&
              (formState.typeOfLoss === TypeOfLoss.NotReceived ||
                formState.typeOfLoss === TypeOfLoss.Counterfeit ||
                formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
                formState.typeOfLoss === TypeOfLoss.AllMailAndPhone))) &&
          !hasInactiveStatus &&
          !hasLostStolenExtStatus &&
          !hasInvalidStatus &&
          !options.isClosedAccount
        )) ? (
        <FraudOnAccountComponent
          disabled={
            isSubmitting ||
            formState.typeOfLoss === TypeOfLoss.FraudApp ||
            (formState.contactId === ContactId.Cardmember &&
              (formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
                formState.typeOfLoss === TypeOfLoss.Counterfeit ||
                formState.typeOfLoss === TypeOfLoss.AllMailAndPhone))
          }
          hideNoButton={
            formState.typeOfLoss === TypeOfLoss.FraudApp ||
            (formState.contactId === ContactId.Cardmember &&
              (formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
                formState.typeOfLoss === TypeOfLoss.Counterfeit ||
                formState.typeOfLoss === TypeOfLoss.AllMailAndPhone))
          }
          fraudOnAccount={formState.fraudOnAccount}
          onChange={(value) => {
            const updateValue = () => {
              const shouldPrefill =
                value === false &&
                formState.contactId === ContactId.Cardmember &&
                (options.externalStatus === "" ||
                  (options.externalStatus === "A" &&
                    options.reasonCode !== "62")) &&
                formState.contactId === ContactId.Cardmember &&
                (formState.typeOfLoss === TypeOfLoss.LostCard ||
                  formState.typeOfLoss === TypeOfLoss.Stolen ||
                  formState.typeOfLoss === TypeOfLoss.NotReceived);

              const defaultWaiveReplacementFeeToYes =
                value === false &&
                options.onlyYesForWaiveReplacementFee &&
                formState.contactId === ContactId.Cardmember &&
                formState.typeOfLoss === TypeOfLoss.LostCard;

              setFormState((prev) => ({
                ...prev,
                fraudOnAccount: value,
                yesFraudOnAccount: undefined,
                noFraudOnAccount:
                  shouldPrefill || defaultWaiveReplacementFeeToYes
                    ? {
                        shipReplacementTo: shouldPrefill
                          ? ShipReplacementTo.Billing
                          : prev.noFraudOnAccount?.shipReplacementTo,
                        billing: {
                          finalInfo: {
                            replacementOption: shouldPrefill
                              ? ReplacementType.Standard
                              : prev.noFraudOnAccount?.billing?.finalInfo
                                  ?.replacementOption,
                            waiveReplacementFee:
                              formState.typeOfLoss === TypeOfLoss.NotReceived ||
                              defaultWaiveReplacementFeeToYes
                                ? true
                                : false,
                            waiveRushFee: defaultWaiveReplacementFeeToYes
                              ? true
                              : undefined,
                            stateOfLoss: shouldPrefill
                              ? options.accountAddress.stateCode
                              : prev.noFraudOnAccount?.billing?.finalInfo
                                  ?.stateOfLoss,
                          },
                        },
                        temporary: defaultWaiveReplacementFeeToYes
                          ? {
                              finalInfo: {
                                ...prev.noFraudOnAccount?.temporary?.finalInfo,
                                waiveReplacementFee: true,
                                waiveRushFee: true,
                              },
                            }
                          : undefined,
                      }
                    : undefined,
              }));
            };

            if (formState.fraudOnAccount !== undefined) {
              setEditedField({
                name: strings.fraudOnAccount,
                code: CHANGE_FIELD_CODES.fraudOnAccount,
                onContinue: updateValue,
              });
            } else {
              updateValue();
            }
          }}
        />
      ) : null}
      {formState.fraudOnAccount &&
      !(
        formState.contactId === ContactId.Cardmember && hasLostStolenExtStatus
      ) ? (
        <>
          <div className={styles.radioContainer}>
            <div className={classNames(styles.label, styles.radioLabel)}>
              {strings.transferToSecurity}
            </div>
            <div>
              <StyledRadioButton
                className={styles.radioButton}
                disabled={isSubmitting}
                checked={!!formState.yesFraudOnAccount?.transferToSecurity}
                label={strings.yes}
                onChange={() => {
                  const updateValue = () => {
                    setFormState((prev) => ({
                      ...prev,
                      yesFraudOnAccount: {
                        transferToSecurity: true,
                      },
                    }));
                    setShowTransferToSecurityModal(true);
                  };

                  if (
                    formState.yesFraudOnAccount?.transferToSecurity !==
                    undefined
                  ) {
                    setEditedField({
                      name: strings.transferToSecurity,
                      code: CHANGE_FIELD_CODES.transferToSecurity,
                      onContinue: updateValue,
                    });
                  } else {
                    updateValue();
                  }
                }}
              />
              <StyledRadioButton
                className={styles.radioButton}
                disabled={isSubmitting}
                checked={
                  formState.yesFraudOnAccount?.transferToSecurity !==
                    undefined &&
                  !formState.yesFraudOnAccount?.transferToSecurity
                }
                label={strings.no}
                onChange={() => {
                  const updateValue = () => {
                    const prefillWithoutAddress =
                      formState.contactId === ContactId.Cardmember &&
                      (hasInvalidStatus ||
                        (options.externalStatus === "A" &&
                          options.reasonCode === "62") ||
                        ((options.externalStatus === "" ||
                          options.externalStatus === "A") &&
                          formState.typeOfLoss === TypeOfLoss.FraudApp));

                    const prefillBillingAddress =
                      !prefillWithoutAddress &&
                      formState.contactId === ContactId.Cardmember &&
                      (options.externalStatus === "" ||
                        options.externalStatus === "A");

                    setFormState((prev) => ({
                      ...prev,
                      yesFraudOnAccount: {
                        ...prev.yesFraudOnAccount,
                        transferToSecurity: false,
                        bestWayToContact:
                          prefillBillingAddress || prefillWithoutAddress
                            ? options.homePhone &&
                              !SPECIAL_FISERV_PHONE_NUMBERS.includes(
                                options.homePhone,
                              )
                              ? WaysToContact.Home
                              : options.workPhone &&
                                  !SPECIAL_FISERV_PHONE_NUMBERS.includes(
                                    options.workPhone,
                                  )
                                ? WaysToContact.Work
                                : WaysToContact.Other
                            : undefined,
                        shipReplacementTo: prefillBillingAddress
                          ? ShipReplacementTo.Billing
                          : undefined,
                        billing: prefillBillingAddress
                          ? {
                              finalInfo: {
                                replacementOption: ReplacementType.Standard,
                                waiveReplacementFee:
                                  formState.typeOfLoss ===
                                    TypeOfLoss.NotReceived ||
                                  formState.typeOfLoss ===
                                    TypeOfLoss.Counterfeit ||
                                  formState.typeOfLoss ===
                                    TypeOfLoss.AccountTakeover ||
                                  formState.typeOfLoss ===
                                    TypeOfLoss.AllMailAndPhone
                                    ? true
                                    : false,
                                lossLocation:
                                  formState.typeOfLoss ===
                                    TypeOfLoss.Counterfeit ||
                                  formState.typeOfLoss ===
                                    TypeOfLoss.AccountTakeover ||
                                  formState.typeOfLoss ===
                                    TypeOfLoss.AllMailAndPhone
                                    ? LossLocation.Unknown
                                    : undefined,
                                stateOfLoss: options.accountAddress.stateCode,
                              },
                            }
                          : undefined,
                        noShipReplacementToFinalInfo: prefillWithoutAddress
                          ? {
                              lossLocation:
                                formState.typeOfLoss === TypeOfLoss.FraudApp ||
                                formState.typeOfLoss ===
                                  TypeOfLoss.Counterfeit ||
                                formState.typeOfLoss ===
                                  TypeOfLoss.AccountTakeover ||
                                formState.typeOfLoss ===
                                  TypeOfLoss.AllMailAndPhone
                                  ? LossLocation.Unknown
                                  : undefined,
                              stateOfLoss: options.accountAddress.stateCode,
                            }
                          : undefined,
                      },
                    }));
                  };
                  if (
                    formState.yesFraudOnAccount?.transferToSecurity !==
                    undefined
                  ) {
                    setEditedField({
                      name: strings.transferToSecurity,
                      code: CHANGE_FIELD_CODES.transferToSecurity,
                      onContinue: updateValue,
                    });
                  } else {
                    updateValue();
                  }
                }}
              />
            </div>
          </div>
          <div className={styles.divider} />
        </>
      ) : null}
      {formState.yesFraudOnAccount?.transferToSecurity === false &&
      !(
        formState.contactId === ContactId.Cardmember && hasLostStolenExtStatus
      ) ? (
        <>
          <PhoneNumberInput
            label={strings.cid}
            variant="horizontal"
            disabled={isSubmitting}
            value={formState.yesFraudOnAccount.cid ?? ""}
            onChange={(value) => {
              setFormState((prev) => ({
                ...prev,
                yesFraudOnAccount: {
                  ...prev.yesFraudOnAccount,
                  cid: value,
                },
              }));
              clearNetworkValidationMessage("cid");
            }}
            validationMessages={validationMessages?.cid}
            showPopover={false}
          />
          <div className={styles.divider} />
          {formState.contactId === ContactId.Cardmember ? (
            <>
              <Select
                variant="horizontal"
                disabled={isSubmitting}
                options={options.waysToContact
                  .filter(
                    (wtc) =>
                      wtc.id === WaysToContact.Other ||
                      (options.homePhone &&
                        !SPECIAL_FISERV_PHONE_NUMBERS.includes(
                          options.homePhone,
                        ) &&
                        wtc.id === WaysToContact.Home) ||
                      (options.workPhone &&
                        !SPECIAL_FISERV_PHONE_NUMBERS.includes(
                          options.workPhone,
                        ) &&
                        wtc.id === WaysToContact.Work),
                  )
                  .map((wtc) => ({
                    value: wtc.id,
                    label: wtc.value,
                  }))}
                selectedValue={formState.yesFraudOnAccount?.bestWayToContact}
                onChange={(value) => {
                  const updateValue = () => {
                    setFormState((prev) => ({
                      ...prev,
                      yesFraudOnAccount: {
                        ...prev.yesFraudOnAccount,
                        bestWayToContact: value,
                      },
                    }));
                  };
                  if (formState.yesFraudOnAccount?.bestWayToContact) {
                    setEditedField({
                      name: strings.bestWayToContact,
                      code: CHANGE_FIELD_CODES.bestWayToContact,
                      onContinue: updateValue,
                    });
                  } else {
                    updateValue();
                  }
                }}
                label={strings.bestWayToContact}
                labelClassName={styles.label}
                placeholder={strings.select}
              />
              {formState.yesFraudOnAccount?.bestWayToContact ===
                WaysToContact.Home && options.homePhone ? (
                <div className={styles.wayToContactPhoneContainer}>
                  <div className={styles.wayToContactPhone}>
                    {maskInput(INPUT_MASKS.phoneNumber, options.homePhone)}
                  </div>
                </div>
              ) : null}
              {formState.yesFraudOnAccount?.bestWayToContact ===
                WaysToContact.Work && options.workPhone ? (
                <div className={styles.wayToContactPhoneContainer}>
                  <div className={styles.wayToContactPhone}>
                    {maskInput(INPUT_MASKS.phoneNumber, options.workPhone)}
                  </div>
                </div>
              ) : null}
              <div className={styles.divider} />
            </>
          ) : null}
        </>
      ) : null}
      {formState.yesFraudOnAccount?.bestWayToContact === WaysToContact.Other ? (
        <>
          <PhoneNumberInput
            label={strings.phoneNumber}
            variant="horizontal"
            disabled={isSubmitting}
            value={formState.yesFraudOnAccount?.otherPhone ?? ""}
            onChange={(value) => {
              setFormState((prev) => ({
                ...prev,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      otherPhone: value,
                    }
                  : undefined,
              }));
              clearNetworkValidationMessage("wayToContactPhone");
            }}
            validationMessages={validationMessages?.wayToContactPhone}
            showPopover={false}
          />
          <div className={styles.divider} />
        </>
      ) : null}
      {!hideShipReplacementTo &&
      ((formState.fraudOnAccount !== undefined &&
        !formState.fraudOnAccount &&
        !hideFieldsAfterFraudOnAccount &&
        (formState.contactId !== ContactId.Cardmember ||
          (!hasLostStolenExtStatus &&
            !(
              hasInvalidStatus && formState.typeOfLoss !== TypeOfLoss.FraudApp
            ) &&
            !options.isClosedAccount))) ||
        formState.yesFraudOnAccount?.cid?.length === PHONE_VALUE_LENGTH ||
        formState.yesFraudOnAccount?.shipReplacementTo ||
        formState.noFraudOnAccount?.shipReplacementTo) ? (
        <ShipReplacementToComponent
          disabled={isSubmitting}
          shipToTypes={options.shipToTypes}
          selectedValue={
            formState.fraudOnAccount
              ? formState.yesFraudOnAccount?.shipReplacementTo
              : formState.noFraudOnAccount?.shipReplacementTo
          }
          onChange={(value: ShipReplacementTo) => {
            const updateValue = () => {
              setFormState((prev) => {
                const prefillWithoutAddress =
                  formState.contactId === ContactId.Cardmember &&
                  (hasInvalidStatus ||
                    (options.externalStatus === "A" &&
                      options.reasonCode === "62") ||
                    ((options.externalStatus === "" ||
                      options.externalStatus === "A") &&
                      formState.typeOfLoss === TypeOfLoss.FraudApp));

                const prefillBillingAddress =
                  !prefillWithoutAddress &&
                  formState.contactId === ContactId.Cardmember &&
                  (options.externalStatus === "" ||
                    options.externalStatus === "A");

                return {
                  ...prev,
                  yesFraudOnAccount: prev.fraudOnAccount
                    ? {
                        ...prev.yesFraudOnAccount,
                        shipReplacementTo: value,
                        billing:
                          prefillBillingAddress &&
                          value === ShipReplacementTo.Billing
                            ? {
                                finalInfo: {
                                  replacementOption: ReplacementType.Standard,
                                  waiveReplacementFee: false,
                                  stateOfLoss: options.accountAddress.stateCode,
                                  ...finalInfo,
                                },
                              }
                            : undefined,
                        temporary:
                          value === ShipReplacementTo.Temporary
                            ? {
                                finalInfo: {
                                  replacementOption: ReplacementType.Standard,
                                  waiveReplacementFee: false,
                                  stateOfLoss: options.accountAddress.stateCode,
                                  ...finalInfo,
                                },
                              }
                            : undefined,
                      }
                    : undefined,
                  noFraudOnAccount: prev.fraudOnAccount
                    ? undefined
                    : {
                        ...prev.noFraudOnAccount,
                        shipReplacementTo: value,
                        billing:
                          prefillBillingAddress &&
                          value === ShipReplacementTo.Billing
                            ? {
                                finalInfo: {
                                  replacementOption: ReplacementType.Standard,
                                  waiveReplacementFee:
                                    onlyShowYesForWaiveReplacementFee
                                      ? true
                                      : false,
                                  stateOfLoss: options.accountAddress.stateCode,
                                  ...finalInfo,
                                },
                              }
                            : undefined,
                        temporary:
                          value === ShipReplacementTo.Temporary
                            ? {
                                finalInfo: {
                                  replacementOption: ReplacementType.Standard,
                                  waiveReplacementFee:
                                    onlyShowYesForWaiveReplacementFee
                                      ? true
                                      : false,
                                  stateOfLoss: options.accountAddress.stateCode,
                                  ...finalInfo,
                                },
                              }
                            : undefined,
                      },
                };
              });
            };
            if (
              formState.yesFraudOnAccount?.shipReplacementTo ||
              formState.noFraudOnAccount?.shipReplacementTo
            ) {
              setEditedField({
                name: strings.shipReplacementTo,
                code: CHANGE_FIELD_CODES.shipReplacementTo,
                onContinue: updateValue,
              });
            } else {
              updateValue();
            }
          }}
        />
      ) : null}
      {!hideFieldsIfFraudAppOrLostCard &&
      formState.contactId !== ContactId.ThirdPartyKnownToCM &&
      (formState.yesFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Billing ||
        formState.noFraudOnAccount?.shipReplacementTo ===
          ShipReplacementTo.Billing) ? (
        <>
          <div className={styles.radioContainer}>
            <div className={classNames(styles.label, styles.radioLabel)}>
              {strings.addressChangeToday}
            </div>
            <StyledRadioButton
              className={styles.radioButton}
              disabled={isSubmitting}
              checked={
                formState.fraudOnAccount
                  ? !!formState.yesFraudOnAccount?.billing?.addressChangeToday
                  : !!formState.noFraudOnAccount?.billing?.addressChangeToday
              }
              label={strings.yes}
              onChange={() => {
                const updateValue = () => {
                  setFormState((prev) => ({
                    ...prev,
                    yesFraudOnAccount: prev.fraudOnAccount
                      ? {
                          ...prev.yesFraudOnAccount,
                          billing: {
                            ...prev.yesFraudOnAccount?.billing,
                            addressChangeToday: true,
                          },
                        }
                      : undefined,
                    noFraudOnAccount: prev.fraudOnAccount
                      ? undefined
                      : {
                          ...prev.noFraudOnAccount,
                          billing: {
                            ...prev.noFraudOnAccount?.billing,
                            addressChangeToday: true,
                          },
                        },
                  }));
                };
                if (
                  formState.yesFraudOnAccount?.billing?.addressChangeToday !==
                    undefined ||
                  formState.noFraudOnAccount?.billing?.addressChangeToday !==
                    undefined
                ) {
                  setEditedField({
                    name: strings.addressChangeToday,
                    code: CHANGE_FIELD_CODES.addressChangeToday,
                    onContinue: updateValue,
                  });
                } else {
                  updateValue();
                }
              }}
            />
            <StyledRadioButton
              className={styles.radioButton}
              disabled={isSubmitting}
              checked={
                formState.fraudOnAccount
                  ? formState.yesFraudOnAccount?.billing?.addressChangeToday ===
                    false
                  : formState.noFraudOnAccount?.billing?.addressChangeToday ===
                    false
              }
              label={strings.no}
              onChange={() => {
                const updateValue = () => {
                  setFormState((prev) => ({
                    ...prev,
                    yesFraudOnAccount: prev.fraudOnAccount
                      ? {
                          ...prev.yesFraudOnAccount,
                          billing: {
                            ...prev.yesFraudOnAccount?.billing,
                            addressChangeToday: false,
                            finalInfo: {
                              ...prev.yesFraudOnAccount?.billing?.finalInfo,
                              replacementOption:
                                prev.yesFraudOnAccount?.billing?.finalInfo
                                  ?.replacementOption ??
                                ReplacementType.Standard,
                            },
                          },
                        }
                      : undefined,
                    noFraudOnAccount: prev.fraudOnAccount
                      ? undefined
                      : {
                          ...prev.noFraudOnAccount,
                          billing: {
                            ...prev.noFraudOnAccount?.billing,
                            addressChangeToday: false,
                            finalInfo: {
                              ...prev.noFraudOnAccount?.billing?.finalInfo,
                              replacementOption:
                                prev.noFraudOnAccount?.billing?.finalInfo
                                  ?.replacementOption ??
                                ReplacementType.Standard,
                            },
                          },
                        },
                  }));
                };
                if (
                  formState.yesFraudOnAccount?.billing?.addressChangeToday !==
                    undefined ||
                  formState.noFraudOnAccount?.billing?.addressChangeToday !==
                    undefined
                ) {
                  setEditedField({
                    name: strings.addressChangeToday,
                    code: CHANGE_FIELD_CODES.addressChangeToday,
                    onContinue: updateValue,
                  });
                } else {
                  updateValue();
                }
              }}
            />
          </div>
          <div className={styles.divider} />
        </>
      ) : null}
      {formState.yesFraudOnAccount?.billing?.addressChangeToday ||
      formState.noFraudOnAccount?.billing?.addressChangeToday ? (
        <>
          <div className={styles.radioContainer}>
            <div className={classNames(styles.label, styles.radioLabel)}>
              {strings.newAddressVerify}
            </div>
            <StyledRadioButton
              className={styles.radioButton}
              disabled={isSubmitting}
              checked={
                formState.fraudOnAccount
                  ? !!formState.yesFraudOnAccount?.billing?.newAddressVerify
                  : !!formState.noFraudOnAccount?.billing?.newAddressVerify
              }
              label={strings.yes}
              onChange={() => {
                const updateValue = () => {
                  setFormState((prev) => ({
                    ...prev,
                    yesFraudOnAccount: prev.fraudOnAccount
                      ? {
                          ...prev.yesFraudOnAccount,
                          billing: {
                            ...prev.yesFraudOnAccount?.billing,
                            newAddressVerify: true,
                          },
                        }
                      : undefined,
                    noFraudOnAccount: prev.fraudOnAccount
                      ? undefined
                      : {
                          ...prev.noFraudOnAccount,
                          billing: {
                            ...prev.noFraudOnAccount?.billing,
                            newAddressVerify: true,
                          },
                        },
                  }));
                };
                if (
                  formState.yesFraudOnAccount?.billing?.newAddressVerify !==
                    undefined ||
                  formState.noFraudOnAccount?.billing?.newAddressVerify !==
                    undefined
                ) {
                  setEditedField({
                    name: strings.newAddressVerify,
                    code: CHANGE_FIELD_CODES.newAddressVerify,
                    onContinue: updateValue,
                  });
                } else {
                  updateValue();
                }
              }}
            />
            <StyledRadioButton
              className={styles.radioButton}
              disabled={isSubmitting}
              checked={
                formState.fraudOnAccount
                  ? formState.yesFraudOnAccount?.billing?.newAddressVerify ===
                    false
                  : formState.noFraudOnAccount?.billing?.newAddressVerify ===
                    false
              }
              label={strings.no}
              onChange={() => {
                const updateValue = () => {
                  setFormState((prev) => ({
                    ...prev,
                    yesFraudOnAccount: prev.fraudOnAccount
                      ? {
                          ...prev.yesFraudOnAccount,
                          billing: {
                            ...prev.yesFraudOnAccount?.billing,
                            newAddressVerify: false,
                          },
                        }
                      : undefined,
                    noFraudOnAccount: prev.fraudOnAccount
                      ? undefined
                      : {
                          ...prev.noFraudOnAccount,
                          billing: {
                            ...prev.noFraudOnAccount?.billing,
                            newAddressVerify: false,
                          },
                        },
                  }));
                };
                if (
                  formState.yesFraudOnAccount?.billing?.newAddressVerify !==
                    undefined ||
                  formState.noFraudOnAccount?.billing?.newAddressVerify !==
                    undefined
                ) {
                  setEditedField({
                    name: strings.newAddressVerify,
                    code: CHANGE_FIELD_CODES.newAddressVerify,
                    onContinue: updateValue,
                  });
                } else {
                  updateValue();
                }
              }}
            />
          </div>
          <div className={styles.divider} />
        </>
      ) : null}
      {
        // only handling case of no fraud: if there was fraud, then we'd transfer to security or require a CID earlier
        formState.noFraudOnAccount?.billing?.newAddressVerify === false ? (
          <>
            <PhoneNumberInput
              label={strings.cid}
              variant="horizontal"
              disabled={isSubmitting}
              value={formState.noFraudOnAccount?.billing?.cid ?? ""}
              validationMessages={validationMessages?.cid}
              showPopover={false}
              onChange={(value) => {
                setFormState((prev) => ({
                  ...prev,
                  noFraudOnAccount: prev.fraudOnAccount
                    ? undefined
                    : {
                        ...prev.noFraudOnAccount,
                        billing: {
                          ...prev.noFraudOnAccount?.billing,
                          cid: value,
                        },
                      },
                }));
                clearNetworkValidationMessage("cid");
              }}
            />
            <div className={styles.divider} />
          </>
        ) : null
      }
      {formState.yesFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Temporary ||
      formState.noFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Temporary ? (
        <>
          <div className={styles.radioContainer}>
            <div className={classNames(styles.label, styles.radioLabel)}>
              {strings.tempAddressVerify}
            </div>
            <StyledRadioButton
              className={styles.radioButton}
              disabled={isSubmitting}
              checked={
                formState.fraudOnAccount
                  ? !!formState.yesFraudOnAccount?.temporary?.tempAddressVerify
                  : !!formState.noFraudOnAccount?.temporary?.tempAddressVerify
              }
              label={strings.yes}
              onChange={() => {
                const updateValue = () => {
                  setFormState((prev) => ({
                    ...prev,
                    yesFraudOnAccount: prev.fraudOnAccount
                      ? {
                          ...prev.yesFraudOnAccount,
                          temporary: {
                            ...prev.yesFraudOnAccount?.temporary,
                            tempAddressVerify: true,
                          },
                        }
                      : undefined,
                    noFraudOnAccount: prev.fraudOnAccount
                      ? undefined
                      : {
                          ...prev.noFraudOnAccount,
                          temporary: {
                            ...prev.noFraudOnAccount?.temporary,
                            tempAddressVerify: true,
                          },
                        },
                  }));
                };
                if (
                  formState.yesFraudOnAccount?.temporary?.tempAddressVerify !==
                    undefined ||
                  formState.noFraudOnAccount?.temporary?.tempAddressVerify !==
                    undefined
                ) {
                  setEditedField({
                    name: strings.tempAddressVerify,
                    code: CHANGE_FIELD_CODES.tempAddressVerify,
                    onContinue: updateValue,
                  });
                } else {
                  updateValue();
                }
              }}
            />
            <StyledRadioButton
              className={styles.radioButton}
              disabled={isSubmitting}
              checked={
                formState.fraudOnAccount
                  ? formState.yesFraudOnAccount?.temporary
                      ?.tempAddressVerify === false
                  : formState.noFraudOnAccount?.temporary?.tempAddressVerify ===
                    false
              }
              label={strings.no}
              onChange={() => {
                const updateValue = () => {
                  setFormState((prev) => ({
                    ...prev,
                    yesFraudOnAccount: prev.fraudOnAccount
                      ? {
                          ...prev.yesFraudOnAccount,
                          temporary: {
                            ...prev.yesFraudOnAccount?.temporary,
                            tempAddressVerify: false,
                          },
                        }
                      : undefined,
                    noFraudOnAccount: prev.fraudOnAccount
                      ? undefined
                      : {
                          ...prev.noFraudOnAccount,
                          temporary: {
                            ...prev.noFraudOnAccount?.temporary,
                            tempAddressVerify: false,
                          },
                        },
                  }));
                };
                if (
                  formState.yesFraudOnAccount?.temporary?.tempAddressVerify !==
                    undefined ||
                  formState.noFraudOnAccount?.temporary?.tempAddressVerify !==
                    undefined
                ) {
                  setEditedField({
                    name: strings.tempAddressVerify,
                    code: CHANGE_FIELD_CODES.tempAddressVerify,
                    onContinue: updateValue,
                  });
                } else {
                  updateValue();
                }
              }}
            />
          </div>
          <div className={styles.divider} />
        </>
      ) : null}
      {
        // only handling case of no fraud: if there was fraud, then we'd transfer to security or require a CID earlier
        formState.noFraudOnAccount?.temporary?.tempAddressVerify === false ? (
          <>
            <PhoneNumberInput
              label={strings.cid}
              variant="horizontal"
              disabled={isSubmitting}
              value={formState.noFraudOnAccount?.temporary?.cid ?? ""}
              validationMessages={validationMessages?.cid}
              showPopover={false}
              onChange={(value) => {
                setFormState((prev) => ({
                  ...prev,
                  noFraudOnAccount: prev.fraudOnAccount
                    ? undefined
                    : {
                        ...prev.noFraudOnAccount,
                        temporary: {
                          ...prev.noFraudOnAccount?.temporary,
                          cid: value,
                        },
                      },
                }));
                clearNetworkValidationMessage("cid");
              }}
            />
            <div className={styles.divider} />
          </>
        ) : null
      }
      {formState.yesFraudOnAccount?.temporary?.tempAddressVerify === false ||
      formState.noFraudOnAccount?.temporary?.tempAddressVerify === false ? (
        <>
          <Select
            variant="horizontal"
            disabled={isSubmitting}
            options={options.tempAddressReasons.map((tar) => ({
              value: tar.id,
              label: tar.value,
            }))}
            selectedValue={
              formState.fraudOnAccount
                ? formState.yesFraudOnAccount?.temporary?.reasonForTempAddress
                : formState.noFraudOnAccount?.temporary?.reasonForTempAddress
            }
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      temporary: {
                        ...prev.yesFraudOnAccount?.temporary,
                        reasonForTempAddress: value,
                      },
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      temporary: {
                        ...prev.noFraudOnAccount?.temporary,
                        reasonForTempAddress: value,
                      },
                    },
              }))
            }
            label={strings.reasonForTempAddress}
            labelClassName={styles.label}
            placeholder={strings.select}
          />
          <div className={styles.divider} />
        </>
      ) : null}
      {formState?.yesFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Temporary ||
      formState?.noFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Temporary ? (
        <>
          <StyledInput
            className={styles.tempAddressField}
            variant="horizontal"
            disabled={isSubmitting}
            innerContentClassName={styles.label}
            label={strings.tempAddress1}
            value={
              (formState.fraudOnAccount
                ? formState.yesFraudOnAccount?.temporary?.tempAddress?.address1
                : formState.noFraudOnAccount?.temporary?.tempAddress
                    ?.address1) ?? ""
            }
            onChange={(e) => {
              setFormState((prev) => ({
                ...prev,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      temporary: {
                        ...prev.yesFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.yesFraudOnAccount?.temporary?.tempAddress,
                          address1: e.target.value,
                        },
                      },
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      temporary: {
                        ...prev.noFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.noFraudOnAccount?.temporary?.tempAddress,
                          address1: e.target.value,
                        },
                      },
                    },
              }));
              clearNetworkValidationMessage("address");
            }}
            showPopover={false}
            validationMessages={
              addressRegex.test(
                (formState.fraudOnAccount
                  ? formState.yesFraudOnAccount?.temporary?.tempAddress
                      ?.address1
                  : formState.noFraudOnAccount?.temporary?.tempAddress
                      ?.address1) ?? "",
              )
                ? undefined
                : [strings.addressInvalidChars]
            }
            maxLength={ADDRESS_LINE_MAX_LENGTH}
          />
          <StyledInput
            className={styles.tempAddressField}
            variant="horizontal"
            disabled={isSubmitting}
            innerContentClassName={styles.label}
            label={strings.tempAddress2}
            value={
              (formState.fraudOnAccount
                ? formState.yesFraudOnAccount?.temporary?.tempAddress?.address2
                : formState.noFraudOnAccount?.temporary?.tempAddress
                    ?.address2) ?? ""
            }
            onChange={(e) => {
              setFormState((prev) => ({
                ...prev,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      temporary: {
                        ...prev.yesFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.yesFraudOnAccount?.temporary?.tempAddress,
                          address2: e.target.value,
                        },
                      },
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      temporary: {
                        ...prev.noFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.noFraudOnAccount?.temporary?.tempAddress,
                          address2: e.target.value,
                        },
                      },
                    },
              }));
              clearNetworkValidationMessage("address");
            }}
            showPopover={false}
            validationMessages={
              addressRegex.test(
                (formState.fraudOnAccount
                  ? formState.yesFraudOnAccount?.temporary?.tempAddress
                      ?.address2
                  : formState.noFraudOnAccount?.temporary?.tempAddress
                      ?.address2) ?? "",
              )
                ? undefined
                : [strings.addressInvalidChars]
            }
            maxLength={ADDRESS_LINE_MAX_LENGTH}
          />
          <StyledZipcodeInput
            className={styles.tempAddressField}
            variant="horizontal"
            disabled={isSubmitting}
            label={
              <div className={styles.zipcodeLabel}>
                {strings.zip}
                {zipcodeToLookup !== null && <Spinner />}
              </div>
            }
            aria-label={strings.zip}
            innerContentClassName={styles.label}
            value={
              (formState.fraudOnAccount
                ? formState.yesFraudOnAccount?.temporary?.tempAddress?.zip
                : formState.noFraudOnAccount?.temporary?.tempAddress?.zip) ?? ""
            }
            onChange={(value) => {
              const oldValue =
                (formState.fraudOnAccount
                  ? formState.yesFraudOnAccount?.temporary?.tempAddress?.zip
                  : formState.noFraudOnAccount?.temporary?.tempAddress?.zip) ??
                "";
              if (
                value.length === ZIPCODE_LENGTH &&
                oldValue.length < ZIPCODE_LENGTH
              ) {
                setZipcodeToLookup(value);
              }

              setFormState((prev) => ({
                ...prev,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      temporary: {
                        ...prev.yesFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.yesFraudOnAccount?.temporary?.tempAddress,
                          zip: value,
                        },
                      },
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      temporary: {
                        ...prev.noFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.noFraudOnAccount?.temporary?.tempAddress,
                          zip: value,
                        },
                      },
                    },
              }));
              clearNetworkValidationMessage("address");
            }}
          />
          <StyledInput
            variant="horizontal"
            className={styles.tempAddressField}
            disabled={isSubmitting}
            innerContentClassName={styles.label}
            label={strings.city}
            value={
              (formState.fraudOnAccount
                ? formState.yesFraudOnAccount?.temporary?.tempAddress?.city
                : formState.noFraudOnAccount?.temporary?.tempAddress?.city) ??
              ""
            }
            onChange={(e) => {
              setFormState((prev) => ({
                ...prev,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      temporary: {
                        ...prev.yesFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.yesFraudOnAccount?.temporary?.tempAddress,
                          city: e.target.value,
                        },
                      },
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      temporary: {
                        ...prev.noFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.noFraudOnAccount?.temporary?.tempAddress,
                          city: e.target.value,
                        },
                      },
                    },
              }));
              clearNetworkValidationMessage("address");
            }}
            showPopover={false}
            validationMessages={
              cityRegex.test(
                (formState.fraudOnAccount
                  ? formState.yesFraudOnAccount?.temporary?.tempAddress?.city
                  : formState.noFraudOnAccount?.temporary?.tempAddress?.city) ??
                  "",
              )
                ? undefined
                : [strings.cityInvalidChars]
            }
            maxLength={CITY_MAX_LENGTH}
          />
          <Select
            variant="horizontal"
            disabled={isSubmitting}
            label={strings.state}
            labelClassName={styles.label}
            placeholder={strings.select}
            options={options.tempAddressStates.map((ls) => ({
              value: ls.id,
              label: ls.value,
            }))}
            selectedValue={
              formState?.fraudOnAccount
                ? formState.yesFraudOnAccount?.temporary?.tempAddress?.state
                : formState.noFraudOnAccount?.temporary?.tempAddress?.state
            }
            onChange={(value) => {
              setFormState((prev) => ({
                ...prev,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      temporary: {
                        ...prev.yesFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.yesFraudOnAccount?.temporary?.tempAddress,
                          state: value,
                        },
                      },
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      temporary: {
                        ...prev.noFraudOnAccount?.temporary,
                        tempAddress: {
                          ...prev.noFraudOnAccount?.temporary?.tempAddress,
                          state: value,
                        },
                      },
                    },
              }));
              clearNetworkValidationMessage("address");
            }}
            validationMessages={validationMessages?.address}
          />
          <div className={styles.divider} />
        </>
      ) : null}
      {
        // hide ship replacement to, OR
        (!hideFieldsIfFraudAppOrLostCard &&
          !hideFieldsIfYesFraudAndNotReceivedOrStolen &&
          hideShipReplacementTo &&
          formState.yesFraudOnAccount?.bestWayToContact) ||
        // no address change today, OR
        formState.yesFraudOnAccount?.billing?.addressChangeToday === false ||
        formState.noFraudOnAccount?.billing?.addressChangeToday === false ||
        // prefilled values, OR
        formState.yesFraudOnAccount?.billing?.finalInfo?.replacementOption !==
          undefined ||
        formState.noFraudOnAccount?.billing?.finalInfo?.replacementOption !==
          undefined ||
        // address verification complete, OR
        formState.yesFraudOnAccount?.billing?.newAddressVerify !== undefined ||
        formState.noFraudOnAccount?.billing?.newAddressVerify ||
        formState.noFraudOnAccount?.billing?.cid?.length ===
          PHONE_VALUE_LENGTH ||
        // shipping to temp addr
        formState.yesFraudOnAccount?.shipReplacementTo ===
          ShipReplacementTo.Temporary ||
        formState.noFraudOnAccount?.shipReplacementTo ===
          ShipReplacementTo.Temporary ? (
          <ReplacementOptionsComponent
            disabled={isSubmitting}
            replacementTypes={options.replacementTypes}
            replacementFee={options.replacementFee}
            rushFee={options.rushFee}
            selectedValue={finalInfo?.replacementOption}
            onChange={(value: ReplacementType) => {
              const updateValue = () => {
                const defaultRushFeesToFalse =
                  (options.externalStatus === "" ||
                    (options.externalStatus === "A" &&
                      options.reasonCode !== "62")) &&
                  formState.contactId === ContactId.Cardmember &&
                  value !== ReplacementType.Standard;

                setFormState((prev) => ({
                  ...prev,
                  yesFraudOnAccount: prev.fraudOnAccount
                    ? {
                        ...prev.yesFraudOnAccount,
                        noShipReplacementToFinalInfo: hideShipReplacementTo
                          ? {
                              ...prev.yesFraudOnAccount
                                ?.noShipReplacementToFinalInfo,
                              replacementOption: value,
                            }
                          : undefined,
                        billing:
                          prev.yesFraudOnAccount?.shipReplacementTo ===
                          ShipReplacementTo.Billing
                            ? {
                                ...prev.yesFraudOnAccount?.billing,
                                finalInfo: {
                                  ...prev.yesFraudOnAccount?.billing?.finalInfo,
                                  replacementOption: value,
                                  waiveReplacementFee:
                                    prev.contactId === ContactId.AuthUser ||
                                    prev.contactId ===
                                      ContactId.ThirdPartyKnownToCM
                                      ? false
                                      : prev.yesFraudOnAccount?.billing
                                          ?.finalInfo?.waiveReplacementFee,
                                  waiveRushFee:
                                    onlyShowYesForWaiveReplacementFee
                                      ? true
                                      : defaultRushFeesToFalse
                                        ? false
                                        : undefined,
                                  stateOfLoss:
                                    prev.contactId === ContactId.AuthUser ||
                                    prev.contactId ===
                                      ContactId.ThirdPartyKnownToCM
                                      ? options.accountAddress.stateCode
                                      : prev.yesFraudOnAccount?.billing
                                          ?.finalInfo?.stateOfLoss,
                                },
                              }
                            : undefined,
                        temporary:
                          prev.yesFraudOnAccount?.shipReplacementTo ===
                          ShipReplacementTo.Temporary
                            ? {
                                ...prev.yesFraudOnAccount?.temporary,
                                finalInfo: {
                                  ...prev.yesFraudOnAccount?.temporary
                                    ?.finalInfo,
                                  replacementOption: value,
                                  waiveReplacementFee:
                                    prev.contactId === ContactId.AuthUser ||
                                    prev.contactId ===
                                      ContactId.ThirdPartyKnownToCM
                                      ? false
                                      : prev.yesFraudOnAccount?.temporary
                                          ?.finalInfo?.waiveReplacementFee,
                                  waiveRushFee:
                                    onlyShowYesForWaiveReplacementFee
                                      ? true
                                      : defaultRushFeesToFalse
                                        ? false
                                        : undefined,
                                  stateOfLoss:
                                    prev.contactId === ContactId.AuthUser ||
                                    prev.contactId ===
                                      ContactId.ThirdPartyKnownToCM
                                      ? options.accountAddress.stateCode
                                      : prev.yesFraudOnAccount?.temporary
                                          ?.finalInfo?.stateOfLoss,
                                },
                              }
                            : undefined,
                      }
                    : undefined,
                  noFraudOnAccount: prev.fraudOnAccount
                    ? undefined
                    : {
                        ...prev.noFraudOnAccount,
                        billing:
                          prev.noFraudOnAccount?.shipReplacementTo ===
                          ShipReplacementTo.Billing
                            ? {
                                ...prev.noFraudOnAccount?.billing,
                                finalInfo: {
                                  ...prev.noFraudOnAccount?.billing?.finalInfo,
                                  replacementOption: value,
                                  waiveReplacementFee:
                                    prev.contactId === ContactId.AuthUser ||
                                    prev.contactId ===
                                      ContactId.ThirdPartyKnownToCM
                                      ? false
                                      : prev.noFraudOnAccount?.billing
                                          ?.finalInfo?.waiveReplacementFee,
                                  stateOfLoss:
                                    prev.contactId === ContactId.AuthUser ||
                                    prev.contactId ===
                                      ContactId.ThirdPartyKnownToCM
                                      ? options.accountAddress.stateCode
                                      : prev.noFraudOnAccount?.billing
                                          ?.finalInfo?.stateOfLoss,
                                  waiveRushFee:
                                    onlyShowYesForWaiveReplacementFee
                                      ? true
                                      : defaultRushFeesToFalse
                                        ? false
                                        : undefined,
                                },
                              }
                            : undefined,
                        temporary:
                          prev.noFraudOnAccount?.shipReplacementTo ===
                          ShipReplacementTo.Temporary
                            ? {
                                ...prev.noFraudOnAccount?.temporary,
                                finalInfo: {
                                  ...prev.noFraudOnAccount?.temporary
                                    ?.finalInfo,
                                  replacementOption: value,
                                  waiveReplacementFee:
                                    prev.contactId === ContactId.AuthUser ||
                                    prev.contactId ===
                                      ContactId.ThirdPartyKnownToCM
                                      ? false
                                      : prev.noFraudOnAccount?.temporary
                                          ?.finalInfo?.waiveReplacementFee,
                                  stateOfLoss:
                                    prev.contactId === ContactId.AuthUser ||
                                    prev.contactId ===
                                      ContactId.ThirdPartyKnownToCM
                                      ? options.accountAddress.stateCode
                                      : prev.noFraudOnAccount?.temporary
                                          ?.finalInfo?.stateOfLoss,
                                  waiveRushFee:
                                    onlyShowYesForWaiveReplacementFee
                                      ? true
                                      : defaultRushFeesToFalse
                                        ? false
                                        : undefined,
                                },
                              }
                            : undefined,
                      },
                }));
              };
              if (finalInfo?.replacementOption) {
                setEditedField({
                  name: strings.replacementOptions,
                  code: CHANGE_FIELD_CODES.replacementOption,
                  onContinue: updateValue,
                });
              } else {
                updateValue();
              }
            }}
          />
        ) : null
      }
      {finalInfo?.replacementOption ||
      hideFieldsIfFraudAppOrLostCard ||
      hideFieldsIfYesFraudAndNotReceivedOrStolen ||
      formState.yesFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Temporary ||
      formState.noFraudOnAccount?.shipReplacementTo ===
        ShipReplacementTo.Temporary ? (
        <>
          {hideFieldsIfFraudAppOrLostCard ||
          hideFieldsIfYesFraudAndNotReceivedOrStolen ? null : onlyShowYesForWaiveReplacementFee ||
            (hasLimitedAccess &&
              (formState.typeOfLoss === TypeOfLoss.NotReceived ||
                formState.typeOfLoss === TypeOfLoss.Counterfeit ||
                formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
                formState.typeOfLoss === TypeOfLoss.AllMailAndPhone)) ||
            (formState.contactId === ContactId.Cardmember &&
              (options.externalStatus === "" ||
                (options.externalStatus === "A" &&
                  options.reasonCode !== "62")) &&
              (formState.typeOfLoss === TypeOfLoss.NotReceived ||
                formState.typeOfLoss === TypeOfLoss.Counterfeit ||
                formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
                formState.typeOfLoss === TypeOfLoss.AllMailAndPhone)) ? (
            <>
              <div className={styles.radioContainer}>
                <div className={classNames(styles.label, styles.radioLabel)}>
                  {strings.waiveReplacementFee}
                </div>
                <StyledRadioButton
                  className={styles.radioButton}
                  disabled={true}
                  checked={true}
                  label={strings.yes}
                  onChange={() => {
                    // do nothing
                  }}
                />
              </div>
              <div className={styles.divider} />
            </>
          ) : (
            <>
              <div className={styles.radioContainer}>
                <div className={classNames(styles.label, styles.radioLabel)}>
                  {strings.waiveReplacementFee}
                </div>
                <StyledRadioButton
                  className={styles.radioButton}
                  disabled={isSubmitting}
                  checked={!!finalInfo?.waiveReplacementFee}
                  label={strings.yes}
                  onChange={() =>
                    setFormState((prev) => ({
                      ...prev,
                      earlyTermination: prev.earlyTermination
                        ? {
                            ...prev.earlyTermination,
                            finalInfo: {
                              ...prev.earlyTermination.finalInfo,
                              waiveReplacementFee: true,
                            },
                          }
                        : undefined,
                      yesFraudOnAccount: prev.fraudOnAccount
                        ? {
                            ...prev.yesFraudOnAccount,
                            noShipReplacementToFinalInfo: hideShipReplacementTo
                              ? {
                                  ...prev.yesFraudOnAccount
                                    ?.noShipReplacementToFinalInfo,
                                  waiveReplacementFee: true,
                                }
                              : undefined,
                            billing:
                              prev.yesFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Billing
                                ? {
                                    ...prev.yesFraudOnAccount?.billing,
                                    finalInfo: {
                                      ...prev.yesFraudOnAccount?.billing
                                        ?.finalInfo,
                                      waiveReplacementFee: true,
                                    },
                                  }
                                : undefined,
                            temporary:
                              prev.yesFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Temporary
                                ? {
                                    ...prev.yesFraudOnAccount?.temporary,
                                    finalInfo: {
                                      ...prev.yesFraudOnAccount?.temporary
                                        ?.finalInfo,
                                      waiveReplacementFee: true,
                                    },
                                  }
                                : undefined,
                          }
                        : undefined,
                      noFraudOnAccount: prev.fraudOnAccount
                        ? undefined
                        : {
                            ...prev.noFraudOnAccount,
                            billing:
                              prev.noFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Billing
                                ? {
                                    ...prev.noFraudOnAccount?.billing,
                                    finalInfo: {
                                      ...prev.noFraudOnAccount?.billing
                                        ?.finalInfo,
                                      waiveReplacementFee: true,
                                    },
                                  }
                                : undefined,
                            temporary:
                              prev.noFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Temporary
                                ? {
                                    ...prev.noFraudOnAccount?.temporary,
                                    finalInfo: {
                                      ...prev.noFraudOnAccount?.temporary
                                        ?.finalInfo,
                                      waiveReplacementFee: true,
                                    },
                                  }
                                : undefined,
                          },
                    }))
                  }
                />
                <StyledRadioButton
                  className={styles.radioButton}
                  disabled={isSubmitting}
                  checked={finalInfo?.waiveReplacementFee === false}
                  label={strings.no}
                  onChange={() =>
                    setFormState((prev) => ({
                      ...prev,
                      earlyTermination: prev.earlyTermination
                        ? {
                            ...prev.earlyTermination,
                            finalInfo: {
                              ...prev.earlyTermination.finalInfo,
                              waiveReplacementFee: false,
                            },
                          }
                        : undefined,
                      yesFraudOnAccount: prev.fraudOnAccount
                        ? {
                            ...prev.yesFraudOnAccount,
                            noShipReplacementToFinalInfo: hideShipReplacementTo
                              ? {
                                  ...prev.yesFraudOnAccount
                                    ?.noShipReplacementToFinalInfo,
                                  waiveReplacementFee: false,
                                }
                              : undefined,
                            billing:
                              prev.yesFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Billing
                                ? {
                                    ...prev.yesFraudOnAccount?.billing,
                                    finalInfo: {
                                      ...prev.yesFraudOnAccount?.billing
                                        ?.finalInfo,
                                      waiveReplacementFee: false,
                                    },
                                  }
                                : undefined,
                            temporary:
                              prev.yesFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Temporary
                                ? {
                                    ...prev.yesFraudOnAccount?.temporary,
                                    finalInfo: {
                                      ...prev.yesFraudOnAccount?.temporary
                                        ?.finalInfo,
                                      waiveReplacementFee: false,
                                    },
                                  }
                                : undefined,
                          }
                        : undefined,
                      noFraudOnAccount: prev.fraudOnAccount
                        ? undefined
                        : {
                            ...prev.noFraudOnAccount,
                            billing:
                              prev.noFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Billing
                                ? {
                                    ...prev.noFraudOnAccount?.billing,
                                    finalInfo: {
                                      ...prev.noFraudOnAccount?.billing
                                        ?.finalInfo,
                                      waiveReplacementFee: false,
                                    },
                                  }
                                : undefined,
                            temporary:
                              prev.noFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Temporary
                                ? {
                                    ...prev.noFraudOnAccount?.temporary,
                                    finalInfo: {
                                      ...prev.noFraudOnAccount?.temporary
                                        ?.finalInfo,
                                      waiveReplacementFee: false,
                                    },
                                  }
                                : undefined,
                          },
                    }))
                  }
                />
              </div>
              <div className={styles.divider} />
            </>
          )}
          {finalInfo?.replacementOption &&
          finalInfo.replacementOption !== ReplacementType.Standard ? (
            onlyShowYesForWaiveReplacementFee ? (
              <>
                <div className={styles.radioContainer}>
                  <div className={classNames(styles.label, styles.radioLabel)}>
                    {strings.waiveRushFee}
                  </div>
                  <StyledRadioButton
                    className={styles.radioButton}
                    disabled={true}
                    checked={true}
                    label={strings.yes}
                    onChange={() => {
                      // do nothing
                    }}
                  />
                </div>
                <div className={styles.divider} />
              </>
            ) : (
              <>
                <div className={styles.radioContainer}>
                  <div className={classNames(styles.label, styles.radioLabel)}>
                    {strings.waiveRushFee}
                  </div>
                  <StyledRadioButton
                    className={styles.radioButton}
                    disabled={isSubmitting}
                    checked={!!finalInfo?.waiveRushFee}
                    label={strings.yes}
                    onChange={() =>
                      setFormState((prev) => ({
                        ...prev,
                        earlyTermination: prev.earlyTermination
                          ? {
                              ...prev.earlyTermination,
                              finalInfo: {
                                ...prev.earlyTermination.finalInfo,
                                waiveRushFee: true,
                              },
                            }
                          : undefined,
                        yesFraudOnAccount: prev.fraudOnAccount
                          ? {
                              ...prev.yesFraudOnAccount,
                              noShipReplacementToFinalInfo:
                                hideShipReplacementTo
                                  ? {
                                      ...prev.yesFraudOnAccount
                                        ?.noShipReplacementToFinalInfo,
                                      waiveRushFee: true,
                                    }
                                  : undefined,
                              billing:
                                prev.yesFraudOnAccount?.shipReplacementTo ===
                                ShipReplacementTo.Billing
                                  ? {
                                      ...prev.yesFraudOnAccount?.billing,
                                      finalInfo: {
                                        ...prev.yesFraudOnAccount?.billing
                                          ?.finalInfo,
                                        waiveRushFee: true,
                                      },
                                    }
                                  : undefined,
                              temporary:
                                prev.yesFraudOnAccount?.shipReplacementTo ===
                                ShipReplacementTo.Temporary
                                  ? {
                                      ...prev.yesFraudOnAccount?.temporary,
                                      finalInfo: {
                                        ...prev.yesFraudOnAccount?.temporary
                                          ?.finalInfo,
                                        waiveRushFee: true,
                                      },
                                    }
                                  : undefined,
                            }
                          : undefined,
                        noFraudOnAccount: prev.fraudOnAccount
                          ? undefined
                          : {
                              ...prev.noFraudOnAccount,
                              billing:
                                prev.noFraudOnAccount?.shipReplacementTo ===
                                ShipReplacementTo.Billing
                                  ? {
                                      ...prev.noFraudOnAccount?.billing,
                                      finalInfo: {
                                        ...prev.noFraudOnAccount?.billing
                                          ?.finalInfo,
                                        waiveRushFee: true,
                                      },
                                    }
                                  : undefined,
                              temporary:
                                prev.noFraudOnAccount?.shipReplacementTo ===
                                ShipReplacementTo.Temporary
                                  ? {
                                      ...prev.noFraudOnAccount?.temporary,
                                      finalInfo: {
                                        ...prev.noFraudOnAccount?.temporary
                                          ?.finalInfo,
                                        waiveRushFee: true,
                                      },
                                    }
                                  : undefined,
                            },
                      }))
                    }
                  />
                  <StyledRadioButton
                    className={styles.radioButton}
                    disabled={isSubmitting}
                    checked={finalInfo?.waiveRushFee === false}
                    label={strings.no}
                    onChange={() =>
                      setFormState((prev) => ({
                        ...prev,
                        earlyTermination: prev.earlyTermination
                          ? {
                              ...prev.earlyTermination,
                              finalInfo: {
                                ...prev.earlyTermination.finalInfo,
                                waiveRushFee: false,
                              },
                            }
                          : undefined,
                        yesFraudOnAccount: prev.fraudOnAccount
                          ? {
                              ...prev.yesFraudOnAccount,
                              noShipReplacementToFinalInfo:
                                hideShipReplacementTo
                                  ? {
                                      ...prev.yesFraudOnAccount
                                        ?.noShipReplacementToFinalInfo,
                                      waiveRushFee: false,
                                    }
                                  : undefined,
                              billing:
                                prev.yesFraudOnAccount?.shipReplacementTo ===
                                ShipReplacementTo.Billing
                                  ? {
                                      ...prev.yesFraudOnAccount?.billing,
                                      finalInfo: {
                                        ...prev.yesFraudOnAccount?.billing
                                          ?.finalInfo,
                                        waiveRushFee: false,
                                      },
                                    }
                                  : undefined,
                              temporary:
                                prev.yesFraudOnAccount?.shipReplacementTo ===
                                ShipReplacementTo.Temporary
                                  ? {
                                      ...prev.yesFraudOnAccount?.temporary,
                                      finalInfo: {
                                        ...prev.yesFraudOnAccount?.temporary
                                          ?.finalInfo,
                                        waiveRushFee: false,
                                      },
                                    }
                                  : undefined,
                            }
                          : undefined,
                        noFraudOnAccount: prev.fraudOnAccount
                          ? undefined
                          : {
                              ...prev.noFraudOnAccount,
                              billing:
                                prev.noFraudOnAccount?.shipReplacementTo ===
                                ShipReplacementTo.Billing
                                  ? {
                                      ...prev.noFraudOnAccount?.billing,
                                      finalInfo: {
                                        ...prev.noFraudOnAccount?.billing
                                          ?.finalInfo,
                                        waiveRushFee: false,
                                      },
                                    }
                                  : undefined,
                              temporary:
                                prev.noFraudOnAccount?.shipReplacementTo ===
                                ShipReplacementTo.Temporary
                                  ? {
                                      ...prev.noFraudOnAccount?.temporary,
                                      finalInfo: {
                                        ...prev.noFraudOnAccount?.temporary
                                          ?.finalInfo,
                                        waiveRushFee: false,
                                      },
                                    }
                                  : undefined,
                            },
                      }))
                    }
                  />
                </div>
                <div className={styles.divider} />
              </>
            )
          ) : null}
          <DateInput
            max={format(new Date(), "yyyy-MM-dd").toString() as DateString}
            showPopover={false}
            validationMessages={
              finalInfo?.dateLost &&
              finalInfo?.dateLost >
                (format(new Date(), "yyyy-MM-dd").toString() as DateString)
                ? [strings.futureDateLostErrorMessage]
                : []
            }
            variant="horizontal"
            disabled={isSubmitting}
            label={strings.dateLost}
            value={finalInfo?.dateLost ?? ""}
            onChangeDate={(value) =>
              setFormState((prev) => ({
                ...prev,
                earlyTermination: prev.earlyTermination
                  ? {
                      ...prev.earlyTermination,
                      finalInfo: {
                        ...prev.earlyTermination.finalInfo,
                        dateLost: value,
                      },
                    }
                  : undefined,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      noShipReplacementToFinalInfo: hideShipReplacementTo
                        ? {
                            ...prev.yesFraudOnAccount
                              ?.noShipReplacementToFinalInfo,
                            dateLost: value,
                          }
                        : undefined,
                      billing:
                        prev.yesFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Billing
                          ? {
                              ...prev.yesFraudOnAccount?.billing,
                              finalInfo: {
                                ...prev.yesFraudOnAccount?.billing?.finalInfo,
                                dateLost: value,
                              },
                            }
                          : undefined,
                      temporary:
                        prev.yesFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Temporary
                          ? {
                              ...prev.yesFraudOnAccount?.temporary,
                              finalInfo: {
                                ...prev.yesFraudOnAccount?.temporary?.finalInfo,
                                dateLost: value,
                              },
                            }
                          : undefined,
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      billing:
                        prev.noFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Billing
                          ? {
                              ...prev.noFraudOnAccount?.billing,
                              finalInfo: {
                                ...prev.noFraudOnAccount?.billing?.finalInfo,
                                dateLost: value,
                              },
                            }
                          : undefined,
                      temporary:
                        prev.noFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Temporary
                          ? {
                              ...prev.noFraudOnAccount?.temporary,
                              finalInfo: {
                                ...prev.noFraudOnAccount?.temporary?.finalInfo,
                                dateLost: value,
                              },
                            }
                          : undefined,
                    },
              }))
            }
          />
          <div className={styles.divider} />
          <Select
            variant="horizontal"
            disabled={
              isSubmitting ||
              (((hasLimitedAccess &&
                (formState.typeOfLoss === TypeOfLoss.Counterfeit ||
                  formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
                  formState.typeOfLoss === TypeOfLoss.AllMailAndPhone)) ||
                ((hasInvalidStatus ||
                  options.externalStatus === "" ||
                  options.externalStatus === "A") &&
                  formState.contactId === ContactId.Cardmember &&
                  (formState.typeOfLoss === TypeOfLoss.FraudApp ||
                    formState.typeOfLoss === TypeOfLoss.Counterfeit ||
                    formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
                    formState.typeOfLoss === TypeOfLoss.AllMailAndPhone) &&
                  formState.fraudOnAccount)) &&
                finalInfo?.lossLocation === LossLocation.Unknown)
            }
            label={strings.lossLocation}
            labelClassName={styles.label}
            placeholder={strings.select}
            options={options.lossLocations.map((ll) => ({
              value: ll.id,
              label: ll.value,
            }))}
            selectedValue={finalInfo?.lossLocation}
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                earlyTermination: prev.earlyTermination
                  ? {
                      ...prev.earlyTermination,
                      finalInfo: {
                        ...prev.earlyTermination.finalInfo,
                        lossLocation: value,
                      },
                    }
                  : undefined,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      noShipReplacementToFinalInfo: hideShipReplacementTo
                        ? {
                            ...prev.yesFraudOnAccount
                              ?.noShipReplacementToFinalInfo,
                            lossLocation: value,
                          }
                        : undefined,
                      billing:
                        prev.yesFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Billing
                          ? {
                              ...prev.yesFraudOnAccount?.billing,
                              finalInfo: {
                                ...prev.yesFraudOnAccount?.billing?.finalInfo,
                                lossLocation: value,
                              },
                            }
                          : undefined,
                      temporary:
                        prev.yesFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Temporary
                          ? {
                              ...prev.yesFraudOnAccount?.temporary,
                              finalInfo: {
                                ...prev.yesFraudOnAccount?.temporary?.finalInfo,
                                lossLocation: value,
                              },
                            }
                          : undefined,
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      billing:
                        prev.noFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Billing
                          ? {
                              ...prev.noFraudOnAccount?.billing,
                              finalInfo: {
                                ...prev.noFraudOnAccount?.billing?.finalInfo,
                                lossLocation: value,
                              },
                            }
                          : undefined,
                      temporary:
                        prev.noFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Temporary
                          ? {
                              ...prev.noFraudOnAccount?.temporary,
                              finalInfo: {
                                ...prev.noFraudOnAccount?.temporary?.finalInfo,
                                lossLocation: value,
                              },
                            }
                          : undefined,
                    },
              }))
            }
          />
          <div className={styles.divider} />
          <Select
            variant="horizontal"
            disabled={isSubmitting}
            label={strings.stateOfLoss}
            labelClassName={styles.label}
            placeholder={strings.select}
            options={options.lossStates.map((ls) => ({
              value: ls.id,
              label: ls.value,
            }))}
            selectedValue={finalInfo?.stateOfLoss}
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                earlyTermination: prev.earlyTermination
                  ? {
                      ...prev.earlyTermination,
                      finalInfo: {
                        ...prev.earlyTermination.finalInfo,
                        stateOfLoss: value,
                      },
                    }
                  : undefined,
                yesFraudOnAccount: prev.fraudOnAccount
                  ? {
                      ...prev.yesFraudOnAccount,
                      noShipReplacementToFinalInfo: hideShipReplacementTo
                        ? {
                            ...prev.yesFraudOnAccount
                              ?.noShipReplacementToFinalInfo,
                            stateOfLoss: value,
                          }
                        : undefined,
                      billing:
                        prev.yesFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Billing
                          ? {
                              ...prev.yesFraudOnAccount?.billing,
                              finalInfo: {
                                ...prev.yesFraudOnAccount?.billing?.finalInfo,
                                stateOfLoss: value,
                              },
                            }
                          : undefined,
                      temporary:
                        prev.yesFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Temporary
                          ? {
                              ...prev.yesFraudOnAccount?.temporary,
                              finalInfo: {
                                ...prev.yesFraudOnAccount?.temporary?.finalInfo,
                                stateOfLoss: value,
                              },
                            }
                          : undefined,
                    }
                  : undefined,
                noFraudOnAccount: prev.fraudOnAccount
                  ? undefined
                  : {
                      ...prev.noFraudOnAccount,
                      billing:
                        prev.noFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Billing
                          ? {
                              ...prev.noFraudOnAccount?.billing,
                              finalInfo: {
                                ...prev.noFraudOnAccount?.billing?.finalInfo,
                                stateOfLoss: value,
                              },
                            }
                          : undefined,
                      temporary:
                        prev.noFraudOnAccount?.shipReplacementTo ===
                        ShipReplacementTo.Temporary
                          ? {
                              ...prev.noFraudOnAccount?.temporary,
                              finalInfo: {
                                ...prev.noFraudOnAccount?.temporary?.finalInfo,
                                stateOfLoss: value,
                              },
                            }
                          : undefined,
                    },
              }))
            }
          />
          <div className={styles.divider} />
          {!(
            formState.contactId === ContactId.Cardmember &&
            (((hasInvalidStatus ||
              options.externalStatus === "" ||
              options.externalStatus === "A") &&
              formState.typeOfLoss === TypeOfLoss.FraudApp) ||
              formState.typeOfLoss === TypeOfLoss.Counterfeit ||
              formState.typeOfLoss === TypeOfLoss.AccountTakeover ||
              formState.typeOfLoss === TypeOfLoss.AllMailAndPhone)
          ) &&
          !(
            hasVeryLimitedAccess &&
            (options.externalStatus === "" ||
              (options.externalStatus === "A" && options.reasonCode !== "62"))
          ) ? (
            <>
              <div className={styles.radioContainer}>
                <div className={classNames(styles.label, styles.radioLabel)}>
                  {strings.pinCompromised}
                </div>
                <StyledRadioButton
                  className={styles.radioButton}
                  disabled={isSubmitting}
                  checked={!!finalInfo?.pinCompromised}
                  label={strings.yes}
                  onChange={() =>
                    setFormState((prev) => ({
                      ...prev,
                      earlyTermination: prev.earlyTermination
                        ? {
                            ...prev.earlyTermination,
                            finalInfo: {
                              ...prev.earlyTermination.finalInfo,
                              pinCompromised: true,
                            },
                          }
                        : undefined,
                      yesFraudOnAccount: prev.fraudOnAccount
                        ? {
                            ...prev.yesFraudOnAccount,
                            noShipReplacementToFinalInfo: hideShipReplacementTo
                              ? {
                                  ...prev.yesFraudOnAccount
                                    ?.noShipReplacementToFinalInfo,
                                  pinCompromised: true,
                                }
                              : undefined,
                            billing:
                              prev.yesFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Billing
                                ? {
                                    ...prev.yesFraudOnAccount?.billing,
                                    finalInfo: {
                                      ...prev.yesFraudOnAccount?.billing
                                        ?.finalInfo,
                                      pinCompromised: true,
                                    },
                                  }
                                : undefined,
                            temporary:
                              prev.yesFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Temporary
                                ? {
                                    ...prev.yesFraudOnAccount?.temporary,
                                    finalInfo: {
                                      ...prev.yesFraudOnAccount?.temporary
                                        ?.finalInfo,
                                      pinCompromised: true,
                                    },
                                  }
                                : undefined,
                          }
                        : undefined,
                      noFraudOnAccount: prev.fraudOnAccount
                        ? undefined
                        : {
                            ...prev.noFraudOnAccount,
                            billing:
                              prev.noFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Billing
                                ? {
                                    ...prev.noFraudOnAccount?.billing,
                                    finalInfo: {
                                      ...prev.noFraudOnAccount?.billing
                                        ?.finalInfo,
                                      pinCompromised: true,
                                    },
                                  }
                                : undefined,
                            temporary:
                              prev.noFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Temporary
                                ? {
                                    ...prev.noFraudOnAccount?.temporary,
                                    finalInfo: {
                                      ...prev.noFraudOnAccount?.temporary
                                        ?.finalInfo,
                                      pinCompromised: true,
                                    },
                                  }
                                : undefined,
                          },
                    }))
                  }
                />
                <StyledRadioButton
                  className={styles.radioButton}
                  disabled={isSubmitting}
                  checked={finalInfo?.pinCompromised === false}
                  label={strings.no}
                  onChange={() =>
                    setFormState((prev) => ({
                      ...prev,
                      earlyTermination: prev.earlyTermination
                        ? {
                            ...prev.earlyTermination,
                            finalInfo: {
                              ...prev.earlyTermination.finalInfo,
                              pinCompromised: false,
                            },
                          }
                        : undefined,
                      yesFraudOnAccount: prev.fraudOnAccount
                        ? {
                            ...prev.yesFraudOnAccount,
                            noShipReplacementToFinalInfo: hideShipReplacementTo
                              ? {
                                  ...prev.yesFraudOnAccount
                                    ?.noShipReplacementToFinalInfo,
                                  pinCompromised: false,
                                }
                              : undefined,
                            billing:
                              prev.yesFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Billing
                                ? {
                                    ...prev.yesFraudOnAccount?.billing,
                                    finalInfo: {
                                      ...prev.yesFraudOnAccount?.billing
                                        ?.finalInfo,
                                      pinCompromised: false,
                                    },
                                  }
                                : undefined,
                            temporary:
                              prev.yesFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Temporary
                                ? {
                                    ...prev.yesFraudOnAccount?.temporary,
                                    finalInfo: {
                                      ...prev.yesFraudOnAccount?.temporary
                                        ?.finalInfo,
                                      pinCompromised: false,
                                    },
                                  }
                                : undefined,
                          }
                        : undefined,
                      noFraudOnAccount: prev.fraudOnAccount
                        ? undefined
                        : {
                            ...prev.noFraudOnAccount,
                            billing:
                              prev.noFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Billing
                                ? {
                                    ...prev.noFraudOnAccount?.billing,
                                    finalInfo: {
                                      ...prev.noFraudOnAccount?.billing
                                        ?.finalInfo,
                                      pinCompromised: false,
                                    },
                                  }
                                : undefined,
                            temporary:
                              prev.noFraudOnAccount?.shipReplacementTo ===
                              ShipReplacementTo.Temporary
                                ? {
                                    ...prev.noFraudOnAccount?.temporary,
                                    finalInfo: {
                                      ...prev.noFraudOnAccount?.temporary
                                        ?.finalInfo,
                                      pinCompromised: false,
                                    },
                                  }
                                : undefined,
                          },
                    }))
                  }
                />
              </div>
              <div className={styles.divider} />
            </>
          ) : null}
          {hasVeryLimitedAccess &&
          (options.externalStatus === "" ||
            (options.externalStatus === "A" && options.reasonCode !== "62")) ? (
            <>
              <label className={classNames(styles.label, styles.textareaLabel)}>
                {strings.additionalInfo}
                <textarea
                  disabled={isSubmitting}
                  className={styles.textareaInput}
                  value={formState.earlyTermination?.additionalInfo}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      earlyTermination: {
                        ...prev.earlyTermination,
                        additionalInfo: e.target.value,
                      },
                    }))
                  }
                />
              </label>
              {formState.earlyTermination?.additionalInfo &&
              !additionalInfoRegex.test(
                formState.earlyTermination?.additionalInfo,
              ) ? (
                <ValidationMessage
                  validationMessages={[strings.additionalInfoValidation]}
                />
              ) : null}
              <div className={styles.divider} />
            </>
          ) : null}
          {formState.fraudOnAccount ? (
            <>
              <label className={classNames(styles.label, styles.textareaLabel)}>
                {strings.fraudCharges}
                <textarea
                  disabled={isSubmitting}
                  className={styles.textareaInput}
                  value={finalInfo?.fraudCharges}
                  maxLength={FRAUD_CHARGES_MAX_LENGTH}
                  onChange={(e) => {
                    clearNetworkValidationMessage("fraudCharges");
                    setFormState((prev) => ({
                      ...prev,
                      yesFraudOnAccount: {
                        ...prev.yesFraudOnAccount,
                        noShipReplacementToFinalInfo: hideShipReplacementTo
                          ? {
                              ...prev.yesFraudOnAccount
                                ?.noShipReplacementToFinalInfo,
                              fraudCharges: e.target.value,
                            }
                          : undefined,
                        billing:
                          prev.yesFraudOnAccount?.shipReplacementTo ===
                          ShipReplacementTo.Billing
                            ? {
                                ...prev.yesFraudOnAccount?.billing,
                                finalInfo: {
                                  ...prev.yesFraudOnAccount?.billing?.finalInfo,
                                  fraudCharges: e.target.value,
                                },
                              }
                            : undefined,
                        temporary:
                          prev.yesFraudOnAccount?.shipReplacementTo ===
                          ShipReplacementTo.Temporary
                            ? {
                                ...prev.yesFraudOnAccount?.temporary,
                                finalInfo: {
                                  ...prev.yesFraudOnAccount?.temporary
                                    ?.finalInfo,
                                  fraudCharges: e.target.value,
                                },
                              }
                            : undefined,
                      },
                    }));
                  }}
                />
              </label>
              {finalInfo?.fraudCharges &&
              !fraudChargesRegex.test(finalInfo.fraudCharges) ? (
                <ValidationMessage
                  validationMessages={[strings.fraudChargesInvalidChars]}
                />
              ) : null}
              <div className={styles.divider} />
            </>
          ) : null}
        </>
      ) : null}
      <BaseModal
        className={styles.modal}
        label={strings.transferToSecurityModalTitle}
        isOpen={showTransferToSecurityModal}
        onRequestClose={() => setShowTransferToSecurityModal(false)}
      >
        <img src={warningIcon} alt="" />
        <div className={styles.modalTitle}>
          {strings.transferToSecurityModalTitle}
        </div>
        <div className={styles.modalDescription}>
          {strings.transferToSecurityModalDescription}
        </div>
        <StyledButton onClick={onTransferToSecurity}>
          {strings.closeTool}
        </StyledButton>
        <StyledButton
          className={styles.goBack}
          variant="secondary"
          onClick={() => setShowTransferToSecurityModal(false)}
        >
          {strings.goBack}
        </StyledButton>
      </BaseModal>
      <BaseModal
        className={styles.modal}
        label={strings.changingFieldName(
          editedField ? editedField.code : "",
          editedField ? editedField.name : "",
        )}
        isOpen={!!editedField}
        onRequestClose={() => setEditedField(null)}
      >
        <img src={warningIcon} alt="" />
        <div className={styles.modalDescription}>
          {strings.changingFieldName(
            editedField ? editedField.code : "",
            editedField ? editedField.name : "",
          )}
        </div>
        <div className={styles.editButtonsContainer}>
          <StyledButton
            variant="secondary"
            onClick={() => {
              if (editedField) {
                editedField.onContinue();
                setEditedField(null);
              }
            }}
          >
            {strings.yes}
          </StyledButton>
          <StyledButton
            className={styles.rightButton}
            onClick={() => setEditedField(null)}
            variant="secondary"
          >
            {strings.no}
          </StyledButton>
        </div>
      </BaseModal>
      <ZipcodeLookupModal
        isOpen={zipcodeToLookup !== null}
        onRequestClose={() => setZipcodeToLookup(null)}
        zipcode={zipcodeToLookup ?? ""}
        onSelectCityState={({ city, state }) => {
          setFormState((prev) => ({
            ...prev,
            yesFraudOnAccount: prev.fraudOnAccount
              ? {
                  ...prev.yesFraudOnAccount,
                  temporary: {
                    ...prev.yesFraudOnAccount?.temporary,
                    tempAddress: {
                      ...prev.yesFraudOnAccount?.temporary?.tempAddress,
                      city,
                      state,
                    },
                  },
                }
              : undefined,
            noFraudOnAccount: prev.fraudOnAccount
              ? undefined
              : {
                  ...prev.noFraudOnAccount,
                  temporary: {
                    ...prev.noFraudOnAccount?.temporary,
                    tempAddress: {
                      ...prev.noFraudOnAccount?.temporary?.tempAddress,
                      city,
                      state,
                    },
                  },
                },
          }));
          setZipcodeToLookup(null);
        }}
      />
    </>
  );
}
